import { 
  collection, 
  query, 
  where, 
  getDocs, 
  getDoc, 
  doc, 
  setDoc, 
  updateDoc, 
  serverTimestamp,
  writeBatch,
  limit,
  orderBy,
  startAfter
} from 'firebase/firestore';
import { db } from '../firebase/config';

/**
 * Wallet felhasználó keresése wallet azonosító alapján
 */
export const findWalletUserByWalletId = async (walletId) => {
  try {
    const q = query(
      collection(db, 'walletUsers'),
      where('walletId', '==', walletId)
    );
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return null;
    }
    
    const walletUser = querySnapshot.docs[0];
    return {
      id: walletUser.id,
      ...walletUser.data()
    };
  } catch (error) {
    console.error('Error finding wallet user:', error);
    throw error;
  }
};

/**
 * Wallet felhasználó keresése email cím alapján
 */
export const findWalletUserByEmail = async (email) => {
  try {
    if (!email) {
      console.log('❌ No email provided to findWalletUserByEmail');
      return null;
    }
    
    console.log(`🔍 Finding wallet user by email: ${email}`);
    
    // Normalizáljuk az email címet
    const normalizedEmail = email.toLowerCase().trim();
    console.log(`🔍 Normalized email: ${normalizedEmail}`);
    
    // Először próbáljuk a normalizált email címmel
    console.log(`🔍 Searching for wallet user with normalized email: ${normalizedEmail}`);
    const normalizedQuery = query(
      collection(db, 'walletUsers'),
      where('email', '==', normalizedEmail)
    );
    const normalizedSnapshot = await getDocs(normalizedQuery);
    console.log(`🔍 Found ${normalizedSnapshot.size} wallet users with normalized email`);
    
    if (!normalizedSnapshot.empty) {
      const walletUser = {
        id: normalizedSnapshot.docs[0].id,
        ...normalizedSnapshot.docs[0].data()
      };
      console.log(`✅ Found wallet user with normalized email: ${walletUser.id}`);
      return walletUser;
    }
    
    // Ha nem találtunk, próbáljuk az eredeti email címmel
    console.log(`🔍 Searching for wallet user with original email: ${email}`);
    const originalQuery = query(
      collection(db, 'walletUsers'),
      where('email', '==', email)
    );
    const originalSnapshot = await getDocs(originalQuery);
    console.log(`🔍 Found ${originalSnapshot.size} wallet users with original email`);
    
    if (!originalSnapshot.empty) {
      const walletUser = {
        id: originalSnapshot.docs[0].id,
        ...originalSnapshot.docs[0].data()
      };
      console.log(`✅ Found wallet user with original email: ${walletUser.id}`);
      return walletUser;
    }
    
    console.log(`❌ No wallet user found with email: ${email}`);
    return null;
  } catch (error) {
    console.error(`❌ Error finding wallet user by email: ${error.message}`);
    console.error(error.stack);
    return null;
  }
};

/**
 * Előny Klub felhasználó keresése wallet azonosító alapján
 */
export const findUserByWalletId = async (walletId) => {
  try {
    const q = query(
      collection(db, 'users'),
      where('walletId', '==', walletId)
    );
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return null;
    }
    
    const user = querySnapshot.docs[0];
    return {
      id: user.id,
      ...user.data()
    };
  } catch (error) {
    console.error('Error finding user by wallet ID:', error);
    throw error;
  }
};

/**
 * Előny Klub felhasználó keresése email cím alapján
 */
export const findUserByEmail = async (email) => {
  try {
    if (!email) {
      console.log('❌ No email provided to findUserByEmail');
      return null;
    }
    
    console.log(`🔍 Finding user by email: ${email}`);
    
    // Normalizáljuk az email címet
    const normalizedEmail = email.toLowerCase().trim();
    console.log(`🔍 Normalized email: ${normalizedEmail}`);
    
    // Először próbáljuk a normalizált email címmel
    console.log(`🔍 Searching for user with normalized email: ${normalizedEmail}`);
    const normalizedQuery = query(
      collection(db, 'users'),
      where('email', '==', normalizedEmail)
    );
    const normalizedSnapshot = await getDocs(normalizedQuery);
    console.log(`🔍 Found ${normalizedSnapshot.size} users with normalized email`);
    
    if (!normalizedSnapshot.empty) {
      const user = {
        id: normalizedSnapshot.docs[0].id,
        ...normalizedSnapshot.docs[0].data()
      };
      console.log(`✅ Found user with normalized email: ${user.id}`);
      return user;
    }
    
    // Ha nem találtunk, próbáljuk az eredeti email címmel
    console.log(`🔍 Searching for user with original email: ${email}`);
    const originalQuery = query(
      collection(db, 'users'),
      where('email', '==', email)
    );
    const originalSnapshot = await getDocs(originalQuery);
    console.log(`🔍 Found ${originalSnapshot.size} users with original email`);
    
    if (!originalSnapshot.empty) {
      const user = {
        id: originalSnapshot.docs[0].id,
        ...originalSnapshot.docs[0].data()
      };
      console.log(`✅ Found user with original email: ${user.id}`);
      return user;
    }
    
    console.log(`❌ No user found with email: ${email}`);
    return null;
  } catch (error) {
    console.error(`❌ Error finding user by email: ${error.message}`);
    console.error(error.stack);
    return null;
  }
};

/**
 * Wallet fiók összekapcsolása Előny Klub fiókkal
 */
export const connectWalletToUser = async (userId, walletId) => {
  try {
    // Wallet felhasználó keresése
    const walletUser = await findWalletUserByWalletId(walletId);
    
    if (!walletUser) {
      throw new Error('Wallet felhasználó nem található');
    }
    
    // Előny Klub felhasználó frissítése
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      walletId: walletId,
      walletConnected: true,
      walletConnectedAt: serverTimestamp(),
      walletBalance: walletUser.balance || 0
    });
    
    // Wallet felhasználó frissítése
    const walletUserRef = doc(db, 'walletUsers', walletUser.id);
    await updateDoc(walletUserRef, {
      elonyKlubId: userId,
      elonyKlubConnected: true,
      elonyKlubConnectedAt: serverTimestamp()
    });
    
    return true;
  } catch (error) {
    console.error('Error connecting wallet to user:', error);
    throw error;
  }
};

/**
 * Wallet fiók leválasztása Előny Klub fiókról
 */
export const disconnectWalletFromUser = async (userId) => {
  try {
    // Előny Klub felhasználó lekérése
    const userRef = doc(db, 'users', userId);
    const userSnap = await getDoc(userRef);
    
    if (!userSnap.exists()) {
      throw new Error('Felhasználó nem található');
    }
    
    const userData = userSnap.data();
    const walletId = userData.walletId;
    
    if (!walletId) {
      throw new Error('Nincs összekapcsolt wallet fiók');
    }
    
    // Előny Klub felhasználó frissítése
    await updateDoc(userRef, {
      walletId: null,
      walletConnected: false,
      walletConnectedAt: null,
      walletBalance: null
    });
    
    // Wallet felhasználó keresése és frissítése
    const walletUser = await findWalletUserByWalletId(walletId);
    
    if (walletUser) {
      const walletUserRef = doc(db, 'walletUsers', walletUser.id);
      await updateDoc(walletUserRef, {
        elonyKlubId: null,
        elonyKlubConnected: false,
        elonyKlubConnectedAt: null
      });
    }
    
    return true;
  } catch (error) {
    console.error('Error disconnecting wallet from user:', error);
    throw error;
  }
};

/**
 * Wallet felhasználók automatikus összekapcsolása email cím alapján
 * @param {Object} options - Opciók
 * @param {number} options.batchSize - Egyszerre feldolgozandó felhasználók száma (alapértelmezett: 100)
 * @param {boolean} options.processAll - Az összes felhasználó feldolgozása, nem csak az összekapcsolatlanok (alapértelmezett: false)
 * @param {boolean} options.forceUpdate - Már összekapcsolt felhasználók frissítése is (alapértelmezett: false)
 * @returns {Promise<Object>} - Az összekapcsolás eredménye
 */
export const autoConnectWalletsByEmail = async (options = {}) => {
  try {
    const {
      batchSize = 100,
      processAll = false,
      forceUpdate = false
    } = options;
    
    console.log('🚀 Starting auto-connect process...');
    console.log('🔧 Options:', { batchSize, processAll, forceUpdate });
    
    // 1. Lekérjük a wallet felhasználókat
    console.log('🔍 Querying wallet users');
    
    // Lekérdezés létrehozása
    let walletUsersQuery;
    if (processAll) {
      // Ha az összes felhasználót feldolgozzuk
      walletUsersQuery = query(
        collection(db, 'walletUsers')
      );
    } else {
      // Csak az összekapcsolatlan felhasználókat dolgozzuk fel
      walletUsersQuery = query(
        collection(db, 'walletUsers'), 
        where('elonyKlubConnected', '==', false)
      );
    }
    
    const walletUsersSnapshot = await getDocs(walletUsersQuery);
    
    if (walletUsersSnapshot.empty) {
      console.log('❌ No wallet users found');
      return {
        success: true,
        connectedCount: 0,
        totalProcessed: 0,
        details: []
      };
    }
    
    const totalWalletUsers = walletUsersSnapshot.size;
    console.log(`✅ Found ${totalWalletUsers} wallet users`);
    
    // 2. Feldolgozzuk a wallet felhasználókat adagokban
    const results = {
      success: true,
      connectedCount: 0,
      totalProcessed: 0,
      details: []
    };
    
    // Wallet felhasználók listája
    const walletUsers = walletUsersSnapshot.docs.map(doc => {
      const data = doc.data();
      // Biztosítjuk, hogy az elonyKlubConnected mező boolean típusú legyen
      return {
        id: doc.id,
        ...data,
        elonyKlubConnected: data.elonyKlubConnected === true
      };
    });
    
    // Adagokban dolgozzuk fel a felhasználókat
    for (let i = 0; i < walletUsers.length; i += batchSize) {
      console.log(`🔄 Processing batch ${Math.floor(i / batchSize) + 1} of ${Math.ceil(walletUsers.length / batchSize)}`);
      
      const batch = writeBatch(db);
      const currentBatch = walletUsers.slice(i, i + batchSize);
      let batchConnectedCount = 0;
      
      // Feldolgozzuk az aktuális adagot
      for (const walletUser of currentBatch) {
        console.log(`\n🔄 Processing wallet user: ${walletUser.id}`);
        console.log(`🔄 Email: ${walletUser.email || 'N/A'}`);
        console.log(`🔄 Wallet ID: ${walletUser.walletId || 'N/A'}`);
        console.log(`🔄 Connected: ${walletUser.elonyKlubConnected}`);
        
        // Ha már össze van kapcsolva és nem kényszerítjük a frissítést, akkor kihagyjuk
        if (walletUser.elonyKlubConnected === true && !forceUpdate) {
          console.log(`⏩ Wallet user ${walletUser.id} is already connected, skipping`);
          results.details.push({
            walletId: walletUser.id,
            email: walletUser.email || 'N/A',
            success: true,
            message: 'Már összekapcsolva'
          });
          results.totalProcessed++;
          continue;
        }
        
        // Ha nincs email cím, átugorjuk
        if (!walletUser.email) {
          console.log(`❌ Wallet user ${walletUser.id} has no email, skipping`);
          results.details.push({
            walletId: walletUser.id,
            email: 'N/A',
            success: false,
            message: 'Nincs email cím'
          });
          results.totalProcessed++;
          continue;
        }
        
        // Normalizáljuk az email címet
        const normalizedEmail = walletUser.email.toLowerCase().trim();
        console.log(`🔍 Normalized email: ${normalizedEmail}`);
        
        try {
          // Keresünk egyező email címmel rendelkező felhasználót
          console.log(`🔍 Searching for user with normalized email: ${normalizedEmail}`);
          const usersQuery = query(
            collection(db, 'users'),
            where('email', '==', normalizedEmail)
          );
          const usersSnapshot = await getDocs(usersQuery);
          console.log(`🔍 Found ${usersSnapshot.size} users with normalized email`);
          
          // Ha nem találtunk, próbáljuk az eredeti email címmel
          if (usersSnapshot.empty) {
            console.log(`❌ No user found with normalized email: ${normalizedEmail}, trying original email`);
            const originalUsersQuery = query(
              collection(db, 'users'),
              where('email', '==', walletUser.email)
            );
            const originalUsersSnapshot = await getDocs(originalUsersQuery);
            console.log(`🔍 Found ${originalUsersSnapshot.size} users with original email`);
            
            if (originalUsersSnapshot.empty) {
              console.log(`❌ No user found with original email: ${walletUser.email} either`);
              results.details.push({
                walletId: walletUser.id,
                email: walletUser.email,
                success: false,
                message: 'Nem található felhasználó ezzel az email címmel'
              });
              results.totalProcessed++;
              continue;
            } else {
              // Találtunk felhasználót az eredeti email címmel
              const user = {
                id: originalUsersSnapshot.docs[0].id,
                ...originalUsersSnapshot.docs[0].data()
              };
              
              // Ellenőrizzük, hogy a felhasználónak van-e uid mezője
              if (!user.uid) {
                console.log(`❌ User ${user.id} has no uid field, skipping`);
                results.details.push({
                  walletId: walletUser.id,
                  email: walletUser.email,
                  userId: user.id,
                  success: false,
                  message: 'A felhasználónak nincs uid mezője'
                });
                results.totalProcessed++;
                continue;
              }
              
              console.log(`✅ Found user with original email: ${user.id}`);
              console.log(`🔍 User data:`, {
                id: user.id,
                email: user.email,
                uid: user.uid
              });
              
              // Frissítjük a wallet felhasználót
              console.log(`🔄 Adding update to batch for wallet: ${walletUser.id}`);
              const walletRef = doc(db, 'walletUsers', walletUser.id);
              batch.update(walletRef, {
                elonyKlubId: user.uid,
                elonyKlubConnected: true,
                elonyKlubConnectedAt: new Date(),
                updatedAt: new Date()
              });
              
              // Frissítjük a felhasználót is
              const userRef = doc(db, 'users', user.id);
              batch.update(userRef, {
                walletId: walletUser.id,
                walletConnected: true,
                walletConnectedAt: new Date(),
                walletBalance: walletUser.balance || 0,
                updatedAt: new Date()
              });
              
              batchConnectedCount++;
              results.connectedCount++;
              results.details.push({
                walletId: walletUser.id,
                email: walletUser.email,
                userId: user.id,
                success: true,
                message: 'Sikeresen összekapcsolva'
              });
              results.totalProcessed++;
            }
          } else {
            // Találtunk felhasználót a normalizált email címmel
            const user = {
              id: usersSnapshot.docs[0].id,
              ...usersSnapshot.docs[0].data()
            };
            
            // Ellenőrizzük, hogy a felhasználónak van-e uid mezője
            if (!user.uid) {
              console.log(`❌ User ${user.id} has no uid field, skipping`);
              results.details.push({
                walletId: walletUser.id,
                email: walletUser.email,
                userId: user.id,
                success: false,
                message: 'A felhasználónak nincs uid mezője'
              });
              results.totalProcessed++;
              continue;
            }
            
            console.log(`✅ Found user with normalized email: ${user.id}`);
            console.log(`🔍 User data:`, {
              id: user.id,
              email: user.email,
              uid: user.uid
            });
            
            // Frissítjük a wallet felhasználót
            console.log(`🔄 Adding update to batch for wallet: ${walletUser.id}`);
            const walletRef = doc(db, 'walletUsers', walletUser.id);
            batch.update(walletRef, {
              elonyKlubId: user.uid,
              elonyKlubConnected: true,
              elonyKlubConnectedAt: new Date(),
              updatedAt: new Date()
            });
            
            // Frissítjük a felhasználót is
            const userRef = doc(db, 'users', user.id);
            batch.update(userRef, {
              walletId: walletUser.id,
              walletConnected: true,
              walletConnectedAt: new Date(),
              walletBalance: walletUser.balance || 0,
              updatedAt: new Date()
            });
            
            batchConnectedCount++;
            results.connectedCount++;
            results.details.push({
              walletId: walletUser.id,
              email: walletUser.email,
              userId: user.id,
              success: true,
              message: 'Sikeresen összekapcsolva'
            });
            results.totalProcessed++;
          }
        } catch (error) {
          console.error(`❌ Error processing wallet user ${walletUser.id}: ${error.message}`);
          results.details.push({
            walletId: walletUser.id,
            email: walletUser.email || 'N/A',
            success: false,
            message: `Hiba: ${error.message}`
          });
          results.totalProcessed++;
        }
      }
      
      // Végrehajtjuk a batch műveletet
      try {
        if (batchConnectedCount > 0) {
          console.log(`🔄 Committing batch with ${batchConnectedCount} updates`);
          await batch.commit();
          console.log(`✅ Successfully connected ${batchConnectedCount} wallet users in this batch`);
        } else {
          console.log('❌ No wallet users were connected in this batch');
        }
      } catch (error) {
        console.error(`❌ Error committing batch: ${error.message}`);
        console.error(error.stack);
        results.success = false;
        results.error = `Hiba a batch végrehajtása során: ${error.message}`;
      }
    }
    
    // Eredmények kiírása
    console.log(`🏁 Auto-connect process completed: ${results.connectedCount} wallet users connected out of ${results.totalProcessed}`);
    
    // Beállítjuk az eredmény üzenetet
    results.message = `${results.connectedCount} wallet felhasználó sikeresen összekapcsolva ${results.totalProcessed} feldolgozottból`;
    
    return results;
  } catch (error) {
    console.error(`❌ Error in auto-connect process: ${error.message}`);
    console.error(error.stack);
    return {
      success: false,
      error: error.message,
      details: []
    };
  }
};

/**
 * Wallet felhasználó bejelentkezése
 */
export const loginWithWallet = async (walletId, password) => {
  try {
    // Itt implementálnánk a wallet-specifikus bejelentkezési logikát
    // Ez függ a wallet rendszer API-jától
    
    // Példa: Wallet felhasználó keresése
    const walletUser = await findWalletUserByWalletId(walletId);
    
    if (!walletUser) {
      throw new Error('Wallet felhasználó nem található');
    }
    
    // Ellenőrizni kell a jelszót vagy más hitelesítési módszert
    // Ez függ a wallet rendszer biztonsági mechanizmusától
    
    // Ha a wallet felhasználó össze van kapcsolva egy Előny Klub fiókkal,
    // akkor visszaadjuk az Előny Klub felhasználót
    if (walletUser.elonyKlubConnected && walletUser.elonyKlubId) {
      const userRef = doc(db, 'users', walletUser.elonyKlubId);
      const userSnap = await getDoc(userRef);
      
      if (userSnap.exists()) {
        return {
          id: userSnap.id,
          ...userSnap.data()
        };
      }
    }
    
    // Ha nincs összekapcsolt fiók, akkor csak a wallet felhasználót adjuk vissza
    return walletUser;
  } catch (error) {
    console.error('Error logging in with wallet:', error);
    throw error;
  }
};

/**
 * Wallet felhasználó kapcsolatának frissítése
 * @param {string} walletUserId - A wallet felhasználó azonosítója
 * @param {string|null} elonyKlubId - Az Előny Klub felhasználó azonosítója (null esetén leválasztás)
 * @param {boolean} connected - Kapcsolat állapota
 * @returns {Promise<boolean>} - Sikeres volt-e a művelet
 */
export const updateWalletUserConnection = async (walletUserId, elonyKlubId, connected) => {
  try {
    console.log(`🔄 Updating wallet connection for wallet user: ${walletUserId}`);
    console.log(`🔄 Előny Klub ID: ${elonyKlubId || 'null'}`);
    console.log(`🔄 Connected: ${connected}`);
    
    // Wallet felhasználó frissítése
    const walletUserRef = doc(db, 'walletUsers', walletUserId);
    const walletUserSnap = await getDoc(walletUserRef);
    
    if (!walletUserSnap.exists()) {
      console.error(`❌ Wallet user not found: ${walletUserId}`);
      throw new Error('Wallet felhasználó nem található');
    }
    
    const walletUser = {
      id: walletUserSnap.id,
      ...walletUserSnap.data()
    };
    
    // Frissítjük a wallet felhasználót
    const updateData = {
      elonyKlubId: elonyKlubId,
      elonyKlubConnected: connected === true,
      updatedAt: new Date()
    };
    
    // Ha kapcsoljuk, akkor beállítjuk a kapcsolat időpontját
    if (connected === true) {
      updateData.elonyKlubConnectedAt = new Date();
    } else {
      updateData.elonyKlubConnectedAt = null;
    }
    
    console.log(`🔄 Updating wallet user with data:`, updateData);
    await updateDoc(walletUserRef, updateData);
    
    // Ha van Előny Klub felhasználó, akkor azt is frissítjük
    if (elonyKlubId) {
      try {
        // Előny Klub felhasználó keresése
        const userQuery = query(
          collection(db, 'users'),
          where('uid', '==', elonyKlubId)
        );
        const userSnapshot = await getDocs(userQuery);
        
        if (!userSnapshot.empty) {
          const user = userSnapshot.docs[0];
          const userRef = doc(db, 'users', user.id);
          
          // Frissítjük a felhasználót
          const userUpdateData = {
            walletId: connected === true ? walletUser.id : null,
            walletConnected: connected === true,
            updatedAt: new Date()
          };
          
          // Ha kapcsoljuk, akkor beállítjuk a kapcsolat időpontját és az egyenleget
          if (connected === true) {
            userUpdateData.walletConnectedAt = new Date();
            userUpdateData.walletBalance = walletUser.balance || 0;
          } else {
            userUpdateData.walletConnectedAt = null;
            userUpdateData.walletBalance = null;
          }
          
          console.log(`🔄 Updating user with data:`, userUpdateData);
          await updateDoc(userRef, userUpdateData);
        } else {
          console.error(`❌ User not found with ID: ${elonyKlubId}`);
          // Nem dobunk hibát, mert a wallet felhasználó frissítése sikeres volt
        }
      } catch (error) {
        console.error(`❌ Error updating user: ${error.message}`);
        // Nem dobunk hibát, mert a wallet felhasználó frissítése sikeres volt
      }
    } else if (connected === false) {
      // Ha leválasztjuk és nincs megadva Előny Klub ID, akkor megkeressük a korábban kapcsolt felhasználót
      if (walletUser.elonyKlubId) {
        try {
          const userQuery = query(
            collection(db, 'users'),
            where('uid', '==', walletUser.elonyKlubId)
          );
          const userSnapshot = await getDocs(userQuery);
          
          if (!userSnapshot.empty) {
            const user = userSnapshot.docs[0];
            const userRef = doc(db, 'users', user.id);
            
            // Frissítjük a felhasználót
            const userUpdateData = {
              walletId: null,
              walletConnected: false,
              walletConnectedAt: null,
              walletBalance: null,
              updatedAt: new Date()
            };
            
            console.log(`🔄 Updating previously connected user with data:`, userUpdateData);
            await updateDoc(userRef, userUpdateData);
          } else {
            console.log(`⚠️ Previously connected user not found with ID: ${walletUser.elonyKlubId}`);
          }
        } catch (error) {
          console.error(`❌ Error updating previously connected user: ${error.message}`);
          // Nem dobunk hibát, mert a wallet felhasználó frissítése sikeres volt
        }
      }
    }
    
    console.log(`✅ Wallet connection updated successfully`);
    return true;
  } catch (error) {
    console.error(`❌ Error updating wallet connection: ${error.message}`);
    console.error(error.stack);
    throw error;
  }
};

/**
 * Összes wallet felhasználó lekérése
 */
export const getAllWalletUsers = async (options = {}) => {
  try {
    const {
      connected,
      pageSize = 20,
      startAfter: startAfterDoc = null
    } = options;

    console.log(`🔍 Getting all wallet users with options:`, options);
    console.log(`🔍 Connected filter: ${connected === undefined ? 'undefined (all)' : connected}`);
    console.log(`🔍 Page size: ${pageSize}`);
    
    // Alap lekérdezés
    let walletUsersQuery = collection(db, 'walletUsers');
    
    // Először ellenőrizzük, hogy van-e egyáltalán dokumentum a gyűjteményben
    try {
      console.log('🔍 Checking if walletUsers collection exists and has documents...');
      const testQuery = query(walletUsersQuery, limit(1));
      const testSnapshot = await getDocs(testQuery);
      console.log(`🔍 walletUsers collection test query returned ${testSnapshot.size} documents`);
      
      if (testSnapshot.empty) {
        console.warn('⚠️ walletUsers collection is empty or does not exist!');
        return {
          users: [],
          lastVisible: null,
          totalCount: 0
        };
      }
    } catch (error) {
      console.error('❌ Error checking walletUsers collection:', error);
    }
    
    let constraints = [];
    
    // Ha van connected szűrő, akkor hozzáadjuk
    if (connected === true) {
      console.log('🔍 Filtering for connected wallet users');
      constraints.push(where('elonyKlubConnected', '==', true));
    } else if (connected === false) {
      console.log('🔍 Filtering for disconnected wallet users');
      constraints.push(where('elonyKlubConnected', '==', false));
    } else {
      console.log('🔍 No connection filter applied, showing all wallet users');
    }
    
    // Rendezés létrehozás dátuma szerint
    // Megjegyzés: Ha a createdAt mező nem létezik minden dokumentumban, akkor a lekérdezés hibát dobhat
    // Ebben az esetben a lekérdezés végrehajtásakor kezeljük a hibát
    console.log('🔍 Adding orderBy constraint: createdAt desc');
    constraints.push(orderBy('createdAt', 'desc'));
    
    // Lapozás - csak akkor adjuk hozzá a limitet, ha nem túl nagy a pageSize
    // Ha a pageSize nagyobb mint 1000, akkor valószínűleg az összes rekordot akarjuk lekérni
    if (pageSize <= 1000) {
      console.log(`🔍 Adding limit: ${pageSize}`);
      constraints.push(limit(pageSize));
    } else {
      console.log(`🔍 Large page size detected (${pageSize}), not adding limit constraint`);
    }
    
    // Ha van startAfter, akkor hozzáadjuk
    if (startAfterDoc) {
      console.log('🔍 Adding startAfter constraint');
      constraints.push(startAfter(startAfterDoc));
    }
    
    // Lekérdezés összeállítása
    const q = query(walletUsersQuery, ...constraints);
    console.log('🔍 Query constraints:', constraints.map(c => c.toString ? c.toString() : c).join(', '));
    
    // Lekérdezés végrehajtása
    console.log('🔍 Executing query...');
    let querySnapshot;
    try {
      querySnapshot = await getDocs(q);
      console.log(`🔍 Query returned ${querySnapshot.size} documents`);
      
      // Naplózzuk az első néhány dokumentumot, ha vannak
      if (querySnapshot.size > 0) {
        console.log('🔍 Sample documents:');
        querySnapshot.docs.slice(0, 2).forEach((doc, index) => {
          console.log(`Document ${index + 1}:`, { id: doc.id, ...doc.data() });
        });
      }
    } catch (error) {
      console.error('❌ Error executing query:', error);
      
      // Ha a hiba a rendezés miatt van, próbáljuk meg rendezés nélkül
      if (error.message && error.message.includes('createdAt')) {
        console.log('⚠️ Error with createdAt field, trying without ordering');
        
        // Új constraints lista rendezés nélkül
        const newConstraints = constraints.filter(c => {
          if (!c.toString) return true;
          const str = c.toString();
          return !str.includes('orderBy');
        });
        
        console.log('🔍 New query constraints:', newConstraints.map(c => c.toString ? c.toString() : c).join(', '));
        
        const newQuery = query(walletUsersQuery, ...newConstraints);
        querySnapshot = await getDocs(newQuery);
        console.log(`🔍 Query without ordering returned ${querySnapshot.size} documents`);
        
        // Naplózzuk az első néhány dokumentumot, ha vannak
        if (querySnapshot.size > 0) {
          console.log('🔍 Sample documents without ordering:');
          querySnapshot.docs.slice(0, 2).forEach((doc, index) => {
            console.log(`Document ${index + 1}:`, { id: doc.id, ...doc.data() });
          });
        }
      } else {
        // Ha más hiba van, dobjuk tovább
        throw error;
      }
    }
    
    // Eredmények feldolgozása
    const users = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Összes rekord számának lekérése
    let totalCount = 0;
    try {
      console.log('🔍 Calculating total count...');
      
      // Ha van connected szűrő, akkor csak azokat számoljuk
      if (connected === true) {
        console.log('🔍 Counting connected wallet users');
        const countQuery = query(
          collection(db, 'walletUsers'),
          where('elonyKlubConnected', '==', true)
        );
        const countSnapshot = await getDocs(countQuery);
        totalCount = countSnapshot.size;
        console.log(`🔍 Total connected wallet users: ${totalCount}`);
      } else if (connected === false) {
        console.log('🔍 Counting disconnected wallet users');
        const countQuery = query(
          collection(db, 'walletUsers'),
          where('elonyKlubConnected', '==', false)
        );
        const countSnapshot = await getDocs(countQuery);
        totalCount = countSnapshot.size;
        console.log(`🔍 Total disconnected wallet users: ${totalCount}`);
      } else {
        // Ha nincs szűrő, akkor az összes rekordot számoljuk
        console.log('🔍 Counting all wallet users');
        const countSnapshot = await getDocs(collection(db, 'walletUsers'));
        totalCount = countSnapshot.size;
        console.log(`🔍 Total wallet users: ${totalCount}`);
      }
    } catch (error) {
      console.error('❌ Error getting total count:', error);
      console.log('⚠️ Using query result size as total count');
      totalCount = querySnapshot.size;
    }
    
    console.log(`✅ Got ${users.length} wallet users out of ${totalCount} total`);
    
    return {
      users,
      lastVisible: querySnapshot.docs.length > 0 ? querySnapshot.docs[querySnapshot.docs.length - 1] : null,
      totalCount
    };
  } catch (error) {
    console.error('❌ Error getting all wallet users:', error);
    console.error('❌ Error details:', error.message, error.stack);
    throw error;
  }
}; 